import { Box, Typography } from '@mui/material';

import { useState, useCallback } from 'react';

import { useDropzone } from 'react-dropzone';

import { fileUploadIcon } from 'common/assets/images';
import {
	DROP_FILE_AREA,
	LARGE_FILE_ERROR,
} from 'common/constants/FileDropAreaConstants';
import { FILE_TOO_LARGE, TOO_MANY_FILES } from 'common/constants/index';

import { MAX_FILE_LIMIT_ERROR } from '../../constants/FileDropAreaConstants';

import styles from './FileDropArea.module.scss';

interface IFileDropAreaProps {
	dragAreaTitle: string;
	handleFileData: (fileData: File[], errorMessage?: string) => void;
	fileData: File[];
	accept?: { [key: string]: string[] };
	maxfileSize?: number; // in MB
	maxFilesLimit?: number;
	onlyTestInitialMaxFileLimitReach?: boolean;
	onlyTestInitialMaxFileSizeReach?: boolean;
}
const FileDropArea = (props: IFileDropAreaProps) => {
	const {
		dragAreaTitle,
		handleFileData,
		accept,
		maxfileSize = 1,
		maxFilesLimit = 1,
		fileData,
		onlyTestInitialMaxFileLimitReach,
		onlyTestInitialMaxFileSizeReach,
	} = props;
	const { DROP_ACTIVATED, DROP_AREA_TEXT, CHOOSE_FILE, CHOOSE_FILES } =
		DROP_FILE_AREA;
	const isMultiFileUpload = maxFilesLimit > 1;
	const [isMaxFileLimitReach, setIsMaxFileLimitReach] =
		useState<boolean>(false);
	const [isLargerFileSize, setIsLargerFileSize] = useState<boolean>(false);

	const maxFileSizeLimit = 1024 * 1024 * maxfileSize;

	const onDrop = useCallback(
		(acceptedFiles: any, rejectedFiles: any) => {
			if (acceptedFiles?.length) {
				const filteredFiles = acceptedFiles.filter(
					(file: any) => file.size <= maxFileSizeLimit,
				);
				let updatedFiles;
				if (!isMultiFileUpload) {
					updatedFiles = filteredFiles.slice(0, maxFilesLimit); // Single upload mode
					setIsMaxFileLimitReach(false);
					setIsLargerFileSize(false);
				} else if (fileData.length + filteredFiles.length <= maxFilesLimit) {
					updatedFiles = [...fileData, ...filteredFiles]; // Multi upload mode
					setIsMaxFileLimitReach(false);
					setIsLargerFileSize(false);
				} else {
					setIsMaxFileLimitReach(true);
					updatedFiles = fileData;
				}
				handleFileData(updatedFiles); // Pass updated files to handleFileData
			}
			// handle reject file condition
			if (rejectedFiles?.length) {
				const hasLargerFileSize =
					rejectedFiles.some(
						(file: any) => file.errors[0].code === FILE_TOO_LARGE,
					) && maxFileSizeLimit;
				const hasMaxFileLimit = rejectedFiles.some(
					(file: any) => file.errors[0].code === TOO_MANY_FILES,
				);
				setIsLargerFileSize(hasLargerFileSize);
				setIsMaxFileLimitReach(hasMaxFileLimit);
			}
		},
		[fileData],
	);
	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		accept: accept || { 'text/csv': ['.csv'] },
		...(maxFileSizeLimit && { maxSize: maxFileSizeLimit }),
		onDrop,
		multiple: isMultiFileUpload,
		maxFiles: maxFilesLimit,
	});
	return (
		<>
			<Box
				className={styles['filedropbox-drop-container']}
				data-testid="filedropbox-drop-container"
				mt={2}
			>
				<div {...getRootProps()}>
					<input
						{...getInputProps()}
						data-testid="dropzone"
						multiple={isMultiFileUpload}
					/>
					<div className={styles['file-drag-area']}>
						{isDragActive ? (
							<p>{DROP_ACTIVATED}</p>
						) : (
							<>
								<img
									className={styles['file-upload-icon']}
									src={fileUploadIcon}
									alt="file upload"
								/>
								<p className={styles['file-upload-para1']}>
									{DROP_AREA_TEXT}{' '}
									<span className={styles['filedropbox-choose']}>
										{isMultiFileUpload ? `${CHOOSE_FILES}` : `${CHOOSE_FILE}`}
									</span>
								</p>
								<p className={styles['file-upload-para2']}>{dragAreaTitle}</p>
							</>
						)}
					</div>
				</div>
			</Box>
			{(isLargerFileSize || onlyTestInitialMaxFileSizeReach) && (
				<Box
					className={styles['filedropbox-drop-limitcontainer']}
					data-testid="filedropbox-drop-limitcontainer"
					mt={2}
				>
					<Typography
						pt={2}
						pb={2}
						className={styles['filedropbox-maxfile-limit']}
					>
						{LARGE_FILE_ERROR(maxfileSize)}
					</Typography>
				</Box>
			)}
			{(isMaxFileLimitReach || onlyTestInitialMaxFileLimitReach) && (
				<Typography
					mt={1}
					mb={1}
					className={styles['filedropbox-maxfile-limit']}
				>
					{MAX_FILE_LIMIT_ERROR(maxFilesLimit)}
				</Typography>
			)}
		</>
	);
};

export default FileDropArea;
